import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Modal, ModalSize } from "components/notifications";
import { Key } from "components/key";
import { ShowHide } from "components/layout";
import "./EarnedRecognition.css";

const defaultFunction = () => { };
const defaultArray = [];

const blankColour = "#FFFFFF";

// Trigger level 0 is the satisfactory band, which the key deliberately does not describe.
const satisfactoryTriggerLevel = 0;
// Level 1 is the band that raises an alert; the pending-breach square is drawn in its colour.
const redTriggerLevel = 1;

// The band names the key shows for each seeded trigger level. The seed's own Trigger_Name reads
// "Yellow Trigger" and is consumed elsewhere, so the display name is resolved here instead.
const triggerLevelNames = {
    1: "Red",
    2: "Amber",
    3: "Yellow"
};

const pendingBreachName = "Pending breach";
const pendingBreachDescription = "A breach in an open period, it can be corrected and has not yet been raised.";

const breachSwatchClassName = "er-key-breach-swatch";

// The drivers KPIs count tachograph days, the maintenance KPIs count fleet size, so the
// small-operator provision is worded per stream.
const smallOperatorDescriptions = {
    Drivers: "Fewer than 25 tachograph days. The period counts once regardless of how many KPIs are breached.",
    Maintenance: "Fewer than 20 vehicles including trailers on the fleet. The period counts once regardless of how many KPIs are breached."
};

// The breach bands come from the seeded trigger thresholds, which repeat the same description and
// colour for every KPI code, so they are reduced to one row per trigger level and listed from the
// least severe band down to the alert-raising one.
function buildBreachItems(thresholds) {
    const bandsByLevel = new Map();

    thresholds.forEach((threshold) => {
        if (threshold.triggerLevel > satisfactoryTriggerLevel && !bandsByLevel.has(threshold.triggerLevel)) {
            bandsByLevel.set(threshold.triggerLevel, threshold);
        }
    });

    if (bandsByLevel.size === 0) {
        return defaultArray;
    }

    const items = Array.from(bandsByLevel.values())
        .sort((first, second) => second.triggerLevel - first.triggerLevel)
        .map((band) => {
            return {
                name: triggerLevelNames[band.triggerLevel],
                description: band.description,
                colours: band.hexColour,
                className: breachSwatchClassName
            };
        });

    const redBand = bandsByLevel.get(redTriggerLevel);

    if (redBand != null) {
        items.push({
            name: pendingBreachName,
            description: pendingBreachDescription,
            // KeyBlock renders its fill through four border colours (top, right, bottom, left), so
            // colouring bottom and left and leaving top and right white gives a bottom-left triangle —
            // the same four values the grid passes for a breach in an open period.
            colours: [blankColour, blankColour, redBand.hexColour, redBand.hexColour],
            className: breachSwatchClassName
        });
    }

    return items;
}

// The key for the Earned Recognition KPI grids: what each period column's shading means and what each
// cell's square means. The period swatches are spans rather than KeyBlocks because KeyBlock sets square
// inline dimensions, so the two-band column shape the design calls for cannot come from a class name.
export function ErKeyModal(
    {
        isOpen = false,
        onClose = defaultFunction,
        stream = "Drivers",
        showSmallOperatorRow = false,
        showAssessmentLines = true,
        thresholds = defaultArray,
        thresholdsLoading = false
    }
) {
    function renderColumnSwatch(stateClass) {
        return (
            <span className={classNames("er-key-column-swatch", stateClass)}>
                <span></span>
                <span></span>
            </span>
        );
    }

    function buildPeriodItems() {
        const items = [
            {
                name: "Open period",
                description: "Still collecting. These figures can still change.",
                swatch: renderColumnSwatch("er-key-column-swatch-open")
            },
            {
                name: "Closed period",
                description: "These figures are final.",
                swatch: renderColumnSwatch("er-key-column-swatch-counting")
            }
        ];

        if (showAssessmentLines) {
            items.push({
                name: "Period boundary",
                description: "These enclose the periods that are counted towards your next assessment.",
                swatch: (
                    <span className="er-key-line-swatch">
                        <span></span>
                        <span></span>
                    </span>
                )
            });
        }

        items.push({
            name: "Alert raised",
            description: "Counting starts again from the period after this one. The Alerts table below the grid shows what caused the alert.",
            swatch: renderColumnSwatch("er-key-column-swatch-alert")
        });

        if (showSmallOperatorRow) {
            items.push({
                name: "Small operator period",
                description: smallOperatorDescriptions[stream],
                swatch: (<span className="er-key-capped-swatch">S</span>)
            });
        }

        return items;
    }

    const squareItems = buildBreachItems(thresholds);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Key"
            size={ModalSize.MEDIUM}
            clickOutsideClose={true}
        >
            <div>
                <div className="spacer-bottom">
                    <h5 className="key">Periods</h5>
                    <Key listVertical hasPadding={false} items={buildPeriodItems()} />
                </div>
                <ShowHide
                    evaluator={thresholdsLoading || squareItems.length > 0}
                    show={(
                        <div>
                            <h5 className="key">Breach Types</h5>
                            <Key listVertical hasPadding={false} loading={thresholdsLoading} items={squareItems} />
                        </div>
                    )}
                />
            </div>
        </Modal>
    );
}

ErKeyModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    stream: PropTypes.oneOf(["Drivers", "Maintenance"]),
    showSmallOperatorRow: PropTypes.bool,
    showAssessmentLines: PropTypes.bool,
    thresholds: PropTypes.array,
    thresholdsLoading: PropTypes.bool
};
